import type {
  AddonPolicySource, AddonSettings, AddonSuggestion, AddonSuggestionType,
  EvaluatedJob, JobRelationship, JobRelationshipType, VehicleContext,
} from '@/types/addonSuggestions';

/**
 * Core-100 R4 — CORE-BP provenance. The version of the built-in rule set. Bump it whenever a built-in
 * keyword list, a reason text or the ADAS coupling changes, so a recorded outcome names the rule that
 * actually produced it.
 */
export const ADDON_RULE_VERSION = 1;

/** The stable identity of the rule for one family, recorded with every outcome. */
export function addonRuleId(type: AddonSuggestionType): string {
  return `core-bp.${type}`;
}
import { filterSuggestionsByVehicleApplicability } from '@/utils/addonVehicleApplicability';

// Built-in keyword lists (can be overridden by shop settings)
const ALIGNMENT_KEYWORDS = [
  'strut', 'shock', 'coilover', 'spring', 'control arm', 'ball joint',
  'tie rod', 'knuckle', 'subframe', 'camber', 'caster', 'toe',
  'rack and pinion', 'steering rack', 'lift kit', 'leveling', 'lowering',
  'suspension', 'sway bar', 'end link', 'bushing'
];

const SAS_KEYWORDS = [
  'steering rack', 'steering column', 'clock spring', 'steering angle',
  'alignment', 'rack and pinion', 'power steering'
];

const TPMS_KEYWORDS = [
  'tire', 'wheel', 'rotation', 'tpms', 'sensor', 'rim', 'balance'
];

const ADAS_KEYWORDS = [
  'windshield', 'front camera', 'radar', 'lidar', 'emblem', 'grille',
  'front bumper', 'adas', 'calibration', 'lane assist', 'collision',
  'adaptive cruise'
];

interface TriggerResult {
  triggered: boolean;
  matchedKeywords: string[];
  /** Core-100 R4 — which policy produced the match (always-prompt term, built-in keyword, coupling). */
  sources?: AddonPolicySource[];
}

/**
 * Check if job names contain any keywords from the provided list
 */
function checkKeywordMatch(
  jobNames: string[],
  keywords: string[],
  alwaysPrompt: string[] = [],
  neverPrompt: string[] = []
): TriggerResult {
  const normalizedJobs = jobNames.map(j => j.toLowerCase());
  const matchedKeywords: string[] = [];

  // Check never-prompt terms first (highest priority)
  for (const term of neverPrompt) {
    const normalizedTerm = term.toLowerCase().trim();
    if (normalizedJobs.some(job => job.includes(normalizedTerm))) {
      return { triggered: false, matchedKeywords: [] };
    }
  }

  const sources: AddonPolicySource[] = [];

  // Check always-prompt terms (second priority)
  for (const term of alwaysPrompt) {
    const normalizedTerm = term.toLowerCase().trim();
    if (normalizedJobs.some(job => job.includes(normalizedTerm))) {
      matchedKeywords.push(term);
      if (!sources.includes('shop_always_prompt')) sources.push('shop_always_prompt');
    }
  }

  // Check built-in keywords
  for (const keyword of keywords) {
    if (normalizedJobs.some(job => job.includes(keyword))) {
      matchedKeywords.push(keyword);
      if (!sources.includes('builtin_keyword')) sources.push('builtin_keyword');
    }
  }

  return {
    triggered: matchedKeywords.length > 0,
    matchedKeywords,
    sources,
  };
}

/**
 * Check if alignment should be suggested
 */
export function checkAlignmentTriggers(
  jobNames: string[],
  settings: AddonSettings
): TriggerResult {
  return checkKeywordMatch(
    jobNames,
    ALIGNMENT_KEYWORDS,
    settings.alwaysPromptTerms,
    settings.neverPromptTerms
  );
}

/**
 * Check if SAS reset should be suggested
 */
export function checkSASTriggers(
  jobNames: string[],
  settings: AddonSettings
): TriggerResult {
  return checkKeywordMatch(
    jobNames,
    SAS_KEYWORDS,
    settings.alwaysPromptTerms,
    settings.neverPromptTerms
  );
}

/**
 * Check if TPMS relearn should be suggested
 */
export function checkTPMSTriggers(
  jobNames: string[],
  settings: AddonSettings
): TriggerResult {
  return checkKeywordMatch(
    jobNames,
    TPMS_KEYWORDS,
    settings.alwaysPromptTerms,
    settings.neverPromptTerms
  );
}

/**
 * Check if ADAS calibration should be suggested
 * Requires: vehicle year >= threshold AND (ADAS keywords OR alignment triggered)
 */
export function checkADASTriggers(
  jobNames: string[],
  vehicleYear: number,
  settings: AddonSettings,
  alignmentTriggered: boolean
): TriggerResult {
  // Year threshold check
  if (vehicleYear < settings.adasYearThreshold) {
    return { triggered: false, matchedKeywords: [] };
  }

  const keywordResult = checkKeywordMatch(
    jobNames,
    ADAS_KEYWORDS,
    settings.alwaysPromptTerms,
    settings.neverPromptTerms
  );

  // ADAS triggers if: ADAS keywords found OR alignment work is happening
  const triggered = keywordResult.triggered || alignmentTriggered;
  const matchedKeywords = keywordResult.matchedKeywords;

  const sources: AddonPolicySource[] = [...(keywordResult.sources ?? [])];
  if (alignmentTriggered && !keywordResult.triggered) {
    matchedKeywords.push('alignment work');
    sources.push('alignment_coupling');
  }

  return { triggered, matchedKeywords, sources };
}

/**
 * WHICH RELATIONSHIPS CAN MEAN "THIS WORK IS TECHNICALLY REQUIRED".
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-BP Slice A — why only `followup`
 * ═══════════════════════════════════════════════════════════════════════════
 * `job_adjacency_map` stores three kinds of edge and they do not mean the same
 * thing. This capability may recognise TECHNICAL OBLIGATION and nothing else —
 * not affinity, not upsell, not "these jobs often occur together". So each kind
 * had to earn its place:
 *
 *   `followup`     directed source -> related, "doing the source is followed by
 *                  the related work". That is precisely the constitutional
 *                  claim: work being defined creates consequential work.
 *                  ACCEPTED.
 *
 *   `related`      generic affinity. The only edges anything in this repository
 *                  has ever written are of this kind, seeded to group
 *                  fuel-system jobs for SCHEDULING. Treating them as obligation
 *                  would turn "these often occur together" into "this is
 *                  required", which is the exact error CORE-BP forbids.
 *                  REFUSED.
 *
 *   `prerequisite` could carry obligation, but its DIRECTION cannot be
 *                  established. Nothing in this repository writes one, no row
 *                  of this type exists, and the column names are symmetric
 *                  enough that "related is a prerequisite of source" and
 *                  "source is a prerequisite of related" are both readable.
 *                  Guessing would invert the obligation and suggest work that
 *                  was already done. REFUSED until something defines it.
 *
 * Refusing two thirds of the vocabulary is the point. An obligation asserted
 * from an edge whose meaning nobody has pinned down is a fabricated obligation.
 */
export const OBLIGATION_RELATIONSHIPS: readonly JobRelationshipType[] = ['followup'];

/** The shop's family-to-catalogue-job mapping, inverted for lookup. */
function familyByCatalogJobId(settings: AddonSettings): Map<string, AddonSuggestionType> {
  const map = new Map<string, AddonSuggestionType>();
  const ids = settings.catalogJobIds ?? {};
  (Object.keys(ids) as AddonSuggestionType[]).forEach((family) => {
    const id = ids[family];
    if (id) map.set(id, family);
  });
  return map;
}

/**
 * Families implied by a real relationship in the catalogue, with the provenance
 * that explains them.
 *
 * Reads the shop's own `catalogJobIds` rather than turning the related job back
 * into a name and searching for that as a substring — which would throw away
 * the identity this slice exists to use.
 */
export function relationshipImpliedFamilies(
  jobs: EvaluatedJob[],
  settings: AddonSettings,
  relationships: JobRelationship[],
): Map<AddonSuggestionType, string[]> {
  const implied = new Map<AddonSuggestionType, string[]>();
  if (!relationships.length) return implied;

  const nameById = new Map<string, string>();
  for (const j of jobs) if (j.catalogJobId) nameById.set(j.catalogJobId, j.name);
  if (!nameById.size) return implied;

  const familyOf = familyByCatalogJobId(settings);

  for (const edge of relationships) {
    if (!OBLIGATION_RELATIONSHIPS.includes(edge.relationshipType)) continue;
    // Direction matters. The job on the ticket must be the SOURCE and the
    // consequential work the RELATED end; the reverse edge would mean this work
    // FOLLOWS something else, which implies nothing about what to do next.
    const sourceName = nameById.get(edge.sourceJobId);
    if (!sourceName) continue;
    const family = familyOf.get(edge.relatedJobId);
    if (!family) continue;

    const provenance = `follow-up of ${sourceName}`;
    const existing = implied.get(family) ?? [];
    if (!existing.includes(provenance)) existing.push(provenance);
    implied.set(family, existing);
  }
  return implied;
}

/** True when shop policy forbids suggesting anything for this work. */
function neverPromptVetoes(jobNames: string[], settings: AddonSettings): boolean {
  const normalized = jobNames.map((j) => j.toLowerCase());
  return (settings.neverPromptTerms ?? []).some((term) => {
    const t = term.toLowerCase().trim();
    return t.length > 0 && normalized.some((job) => job.includes(t));
  });
}

/**
 * Main evaluation function — determines which add-ons to suggest.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE ORDER OF AUTHORITY, WHICH IS THE SHOP'S AND NOT THE ENGINE'S
 * ═══════════════════════════════════════════════════════════════════════════
 *   1. never-prompt   absolute. Defeats keywords AND relationship evidence. A
 *                     shop that says "never suggest this" has said so about
 *                     every reason the engine might have, not only the ones it
 *                     had when the setting was written.
 *   2. always-prompt  explicit shop policy, above the built-in rules.
 *   3. relationships  structured evidence from the catalogue.
 *   4. keywords       the conservative fallback, and the ONLY evidence
 *                     available for free-text work with no catalogue identity.
 *
 * Evidence is additive and a family is suggested at most once: two sources
 * reaching the same conclusion is stronger evidence, not two jobs.
 *
 * `vehicle.make` and `vehicle.model` now participate, as a REFINEMENT and only
 * ever to suppress — CORE-BP item 2. Nothing in this repository can say
 * truthfully that a make or model makes a service technically required, and
 * inventing that from general automotive knowledge is what this capability must
 * never do. So vehicle identity cannot RAISE a suggestion here; it can only
 * carry a shop's own "do not ask me about this on that vehicle", applied after
 * the evidence above has already decided what is technically applicable.
 *
 * That is where the constitution puts it: "vehicle attributes → refine where
 * the obligation is vehicle-dependent". It sits below the absolute never-prompt
 * veto and above nothing — it refines whatever the evidence produced, whether
 * that was always-prompt, a catalogue relationship, or keywords.
 */
export function evaluateAddonSuggestions(
  jobs: EvaluatedJob[],
  vehicle: VehicleContext,
  settings: AddonSettings,
  relationships: JobRelationship[] = [],
): AddonSuggestion[] {
  if (!settings.featureEnabled || jobs.length === 0) {
    return [];
  }

  const jobNames = jobs.map((j) => j.name);
  const vehicleYear = vehicle.year;

  // Shop veto first, so nothing below can reintroduce a suppressed family.
  const vetoed = neverPromptVetoes(jobNames, settings);
  const implied = vetoed
    ? new Map<AddonSuggestionType, string[]>()
    : relationshipImpliedFamilies(jobs, settings, relationships);

  const suggestions: AddonSuggestion[] = [];
  const add = (
    type: AddonSuggestionType,
    label: string,
    reason: string,
    defaultChecked: boolean,
    triggers: string[],
    sources: AddonPolicySource[] = [],
  ) => {
    const merged = [...triggers, ...(implied.get(type) ?? [])]
      .filter((t, i, a) => a.indexOf(t) === i);
    // Core-100 R4 — the rule and the evidence that fired it travel with the suggestion, so the outcome
    // can record them (CORE-BP "Provenance / Explainability").
    const policySources = [...sources, ...(implied.has(type) ? ['catalog_followup' as const] : [])]
      .filter((s, i, a) => a.indexOf(s) === i);
    suggestions.push({
      type, label, reason, defaultChecked, matchedTriggers: merged,
      provenance: { ruleId: addonRuleId(type), ruleVersion: ADDON_RULE_VERSION, policySources },
    });
  };

  const alignmentResult = checkAlignmentTriggers(jobNames, settings);
  const alignmentTriggered = alignmentResult.triggered || implied.has('alignment');
  if (alignmentTriggered) {
    add('alignment', 'Four-wheel alignment',
      'Suspension/steering work changes toe/camber/caster; alignment prevents pull & tire wear.',
      settings.alignmentDefaultChecked, alignmentResult.matchedKeywords, alignmentResult.sources);
  }

  const sasResult = checkSASTriggers(jobNames, settings);
  if (sasResult.triggered || implied.has('sas')) {
    add('sas', 'Steering Angle Sensor (SAS) reset',
      'Rack/column/alignment can invalidate SAS; reset restores stability control behavior.',
      settings.sasDefaultChecked, sasResult.matchedKeywords, sasResult.sources);
  }

  const tpmsResult = checkTPMSTriggers(jobNames, settings);
  if (tpmsResult.triggered || implied.has('tpms')) {
    add('tpms', 'TPMS relearn',
      'Tire/wheel work often requires relearn so the warning light stays off.',
      settings.tpmsDefaultChecked, tpmsResult.matchedKeywords, tpmsResult.sources);
  }

  // ADAS keeps its coupling to alignment, and the coupling now follows the
  // COMBINED alignment verdict — relationship-implied alignment is still
  // alignment work, and the front-end geometry does not care which kind of
  // evidence found it. The year threshold still gates every path, including the
  // relationship one.
  const adasResult = checkADASTriggers(jobNames, vehicleYear, settings, alignmentTriggered);
  const adasImplied = implied.has('adas') && vehicleYear >= settings.adasYearThreshold;
  if (adasResult.triggered || adasImplied) {
    const triggers = [...adasResult.matchedKeywords, ...(implied.get('adas') ?? [])];
    const shouldDefaultCheck = settings.precisionFirstMode
      ? triggers.length > 1
      : settings.adasDefaultChecked;
    add('adas', 'ADAS calibration',
      'Camera/radar alignment can shift after windshield/suspension/front-end work.',
      shouldDefaultCheck, adasResult.matchedKeywords, adasResult.triggered ? adasResult.sources : []);
  }

  // CORE-BP item 2 — the shop's vehicle policy, applied last and only ever to
  // remove. Applied HERE, inside the evaluator, so that every caller gets it by
  // construction: ticket creation and the post-add path cannot diverge on what
  // a shop's vehicle exception means, because neither of them implements it.
  return filterSuggestionsByVehicleApplicability(
    suggestions, vehicle, settings.vehicleExceptions);
}

/**
 * Get the suggestion type label for display
 */
export function getSuggestionLabel(type: AddonSuggestionType): string {
  const labels: Record<AddonSuggestionType, string> = {
    alignment: 'Four-wheel alignment',
    sas: 'Steering Angle Sensor (SAS) reset',
    adas: 'ADAS calibration',
    tpms: 'TPMS relearn'
  };
  return labels[type];
}
